import GlobeIcon from 'svg/knowledgeGraph/globe.svg';
import globeIcon from 'svg/knowledgeGraph/globe.svg?raw';
import BooleanIcon from 'svg/knowledgeGraph/boolean.svg';
import booleanIcon from 'svg/knowledgeGraph/boolean.svg?raw';
import NumberIcon from 'svg/knowledgeGraph/number.svg';
import numberIcon from 'svg/knowledgeGraph/number.svg?raw';
import CalendarIcon from 'svg/knowledgeGraph/calendar.svg';
import calendarIcon from 'svg/knowledgeGraph/calendar.svg?raw';
import MoneyIcon from 'svg/knowledgeGraph/money.svg';
import moneyIcon from 'svg/knowledgeGraph/money.svg?raw';
import PersonIcon from 'svg/knowledgeGraph/person.svg';
import personIcon from 'svg/knowledgeGraph/person.svg?raw';
import TextIcon from 'svg/knowledgeGraph/text.svg';
import textIcon from 'svg/knowledgeGraph/text.svg?raw';
import Category from '@mui/icons-material/Category';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import LinkIcon from '@mui/icons-material/LinkRounded';
import FunctionsIcon from '@mui/icons-material/FunctionsRounded';
import FunctionIcon from '../../svg/knowledgeGraph/function.svg';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FolderIcon from '@mui/icons-material/FolderRounded';
import ChatIcon from '@mui/icons-material/ChatRounded';
import QuestionAnswer from '@mui/icons-material/QuestionAnswerRounded';
import Dashboard from '@mui/icons-material/Dashboard';
import VocabularyIcon from '@mui/icons-material/AbcRounded';
import DiscoveryIcon from '../../svg/knowledgeGraph/discovery.svg';
import RuleIcon from '@mui/icons-material/RuleRounded';
import BookIcon from '@mui/icons-material/BookRounded';
import ViewColumnIcon from '@mui/icons-material/ViewColumnRounded';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadlineRounded';
import SchemaIcon from '@mui/icons-material/SchemaRounded';
import StorageIcon from '@mui/icons-material/StorageRounded';
import EvolutionIcon from '@mui/icons-material/UpdateRounded';

export const OntologyEntity = 'meta:KB_Entity';
export const OntologyClass = 'meta:KB_Class';
export const OntologyDate = 'meta:Date';
export const OntologyBoolean = 'xsd:boolean';

/** Configuration for ontology types used in the knowledge graph
 *
 * The keys are shortened URIs of ontology types,
 * which are specific ontology resource like "onto:Money" or just the resource type like "xsd:string".
 *
 * The values are configs consisting of:
 * - `icon`: The SVG icon as a string for use in HTML
 * - `componentIcon`: The React component for the icon, used in JSX
 * - `color`: Optional color for the icon, used in styles
 * - `translationKey`: Key for translation, used in i18n
 */
export const OntologyTypeConfigs = {
  'xsd:integer': {
    icon: numberIcon,
    componentIcon: NumberIcon,
    color: '#FFA34C',
    translationKey: 'integer'
  },
  'xsd:double': {
    icon: numberIcon,
    componentIcon: NumberIcon,
    color: '#FFA34C',
    translationKey: 'decimal'
  },
  'xsd:long': {
    icon: numberIcon,
    componentIcon: NumberIcon,
    color: '#FFA34C',
    translationKey: 'integer'
  },
  'xsd:string': {
    icon: textIcon,
    componentIcon: TextIcon,
    color: '#5CACE7',
    translationKey: 'text'
  },
  'xsd:boolean': {
    icon: booleanIcon,
    componentIcon: BooleanIcon,
    color: '#4ca356',
    translationKey: 'boolean'
  },
  'meta:Date': {
    icon: calendarIcon,
    componentIcon: CalendarIcon,
    color: '#E36B67',
    translationKey: 'date'
  },
  'onto:Location': {
    icon: globeIcon,
    componentIcon: GlobeIcon,
    translationKey: 'location'
  },
  'onto:Money': {
    icon: moneyIcon,
    componentIcon: MoneyIcon,
    color: '#FFA34C',
    translationKey: 'money'
  },
  'onto:Customer': {
    icon: personIcon,
    componentIcon: PersonIcon,
    translationKey: 'customer'
  },
  'meta:KB_Entity': {
    color: '#172e73',
    componentIcon: props => <CategoryOutlined {...props} />
  },
  'meta:KB_Class': {
    componentIcon: props => <Category {...props} />
  },
  none: null
};

/** Icons (React component) for different VKL types used in knowledge graph. */
export const IconsForVklType = {
  class: OntologyTypeConfigs['meta:KB_Class'].componentIcon,
  entity: OntologyTypeConfigs['meta:KB_Entity'].componentIcon,
  subclass: OntologyTypeConfigs['meta:KB_Class'].componentIcon,
  number: OntologyTypeConfigs['xsd:integer'].componentIcon,
  integer: OntologyTypeConfigs['xsd:integer'].componentIcon,
  long: OntologyTypeConfigs['xsd:long'].componentIcon,
  string: OntologyTypeConfigs['xsd:string'].componentIcon,
  boolean: OntologyTypeConfigs['xsd:boolean'].componentIcon,
  date: OntologyTypeConfigs['meta:Date'].componentIcon,
  rule: RuleIcon,
  macro_grammar: BookIcon,
  column: ViewColumnIcon,
  table: ViewHeadlineIcon,
  schema: SchemaIcon,
  database: StorageIcon,
  storage: StorageIcon,
  relationship: LinkIcon,
  measure: FunctionsIcon, // Sum icon (big sigma)
  function: FunctionIcon, // Function icon (f x)
  variable: AttachMoneyIcon, // $ icon
  discovery_category: FolderIcon,
  discovery_topic: FolderIcon,
  discovery_question: ChatIcon,
  ground_truth: QuestionAnswer,
  rephrase_ground_truth: QuestionAnswer,
  board: Dashboard
};

/** Icons (React component) for different file types in the knowledge graph.
 *
 * These are mostly the icons corresponding to the main VKL types in `IconsForVklType`.
 */
export const IconsForFileType = {
  Entities: OntologyTypeConfigs['meta:KB_Entity'].componentIcon,
  SyncedEntities: OntologyTypeConfigs['meta:KB_Entity'].componentIcon,
  OldEntities: OntologyTypeConfigs['meta:KB_Entity'].componentIcon,
  OldSyncedEntities: OntologyTypeConfigs['meta:KB_Entity'].componentIcon,
  Class: OntologyTypeConfigs['meta:KB_Class'].componentIcon,
  Relation: LinkIcon,
  Computation: FunctionIcon,
  Vocabulary: VocabularyIcon,
  Discovery: DiscoveryIcon,
  QuestionTemplate: ChatIcon,
  Rule: RuleIcon,
  MacroGrammar: BookIcon,
  Table: ViewHeadlineIcon,
  Schema: SchemaIcon,
  Database: StorageIcon,
  Storage: StorageIcon,
  Definition: BookIcon,
  GroundTruth: QuestionAnswer,
  Ontology: SchemaIcon,
  OntologyEntities: SchemaIcon,
  Evolutions: EvolutionIcon,
  Board: Dashboard,
  Variables: AttachMoneyIcon
};

export const hasCustomGroup = group => (OntologyTypeConfigs[group] ? group : 'none');
